import React, { useState } from 'react';
import MarketingLayout from './MarketingLayout';

export default function Contact() {
  const [form, setForm] = useState({ name: '', email: '', subject: '', message: '' });
  const [status, setStatus] = useState(null);
  const [sending, setSending] = useState(false);

  const handleChange = e => setForm(f => ({ ...f, [e.target.name]: e.target.value }));

  const handleSubmit = async e => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      setStatus({ ok: false, text: 'Please fill in all required fields.' });
      return;
    }
    setSending(true);
    setStatus(null);
    // Opens the user's email client with pre-filled subject/body as fallback
    const subject = encodeURIComponent(form.subject || `Contact from ${form.name}`);
    const body = encodeURIComponent(`Name: ${form.name}\nEmail: ${form.email}\n\n${form.message}`);
    window.location.href = `mailto:support@clicksaver.app?subject=${subject}&body=${body}`;
    setStatus({ ok: true, text: "Your email client has opened. Send the email to reach our support team." });
    setSending(false);
  };

  const inputStyle = {
    width: '100%', padding: '11px 14px', fontSize: 14,
    background: '#f6f8fa', border: '1px solid #d0d7de',
    borderRadius: 8, color: '#1f2328', fontFamily: 'inherit',
    boxSizing: 'border-box',
  };

  return (
    <MarketingLayout>
      <div style={{ maxWidth: 1100, margin: '0 auto', padding: '60px 24px 80px' }}>

        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: 56 }}>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: 'rgba(37,99,235,0.08)', border: '1px solid rgba(37,99,235,0.15)', borderRadius: 20, padding: '4px 14px', fontSize: 12, fontWeight: 600, color: '#2563eb', marginBottom: 16 }}>
            CONTACT US
          </div>
          <h1 style={{ fontSize: 42, fontWeight: 800, color: '#0f172a', marginBottom: 16, lineHeight: 1.2 }}>
            Get in Touch
          </h1>
          <p style={{ fontSize: 17, color: '#64748b', maxWidth: 520, margin: '0 auto', lineHeight: 1.7 }}>
            Have a question, need help, or want to discuss your account? We're here for you.
          </p>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1.6fr', gap: 40, alignItems: 'start' }}>

          {/* Left — contact info */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>

            {[
              {
                icon: '✉',
                title: 'Email Support',
                desc: 'Send us an email and we\'ll respond within 24 hours.',
                value: 'support@clicksaver.app',
                href: 'mailto:support@clicksaver.app',
              },
              {
                icon: '⏱',
                title: 'Response Time',
                desc: 'We respond to all enquiries within 24 hours on business days.',
                value: 'Mon – Fri, 9am – 6pm AEST',
              },
              {
                icon: '💬',
                title: 'In-App Chat',
                desc: 'Already a customer? Use the live chat inside your dashboard for fastest support.',
                value: 'Available after login',
              },
            ].map((item, i) => (
              <div key={i} style={{
                padding: '20px 24px', background: '#fff', borderRadius: 12,
                border: '1px solid #e2e8f0', boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
              }}>
                <div style={{ fontSize: 24, marginBottom: 10 }}>{item.icon}</div>
                <div style={{ fontSize: 14, fontWeight: 700, color: '#0f172a', marginBottom: 4 }}>{item.title}</div>
                <div style={{ fontSize: 13, color: '#64748b', lineHeight: 1.6, marginBottom: 8 }}>{item.desc}</div>
                {item.href ? (
                  <a href={item.href} style={{ fontSize: 14, fontWeight: 600, color: '#2563eb', textDecoration: 'none' }}>{item.value}</a>
                ) : (
                  <div style={{ fontSize: 13, fontWeight: 600, color: '#475569' }}>{item.value}</div>
                )}
              </div>
            ))}
          </div>

          {/* Right — contact form */}
          <div style={{
            background: '#fff', borderRadius: 16, border: '1px solid #e2e8f0',
            padding: '36px 36px', boxShadow: '0 4px 20px rgba(0,0,0,0.06)',
          }}>
            <h2 style={{ fontSize: 20, fontWeight: 700, color: '#0f172a', marginBottom: 6 }}>Send a Message</h2>
            <p style={{ fontSize: 13, color: '#64748b', marginBottom: 28 }}>
              Fill out the form below and we'll get back to you at <strong>support@clicksaver.app</strong>
            </p>

            <form onSubmit={handleSubmit}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 14 }}>
                <div>
                  <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 6 }}>Full Name *</label>
                  <input name="name" value={form.name} onChange={handleChange} placeholder="John Smith" style={inputStyle} required />
                </div>
                <div>
                  <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 6 }}>Email Address *</label>
                  <input name="email" type="email" value={form.email} onChange={handleChange} placeholder="john@company.com" style={inputStyle} required />
                </div>
              </div>

              <div style={{ marginBottom: 14 }}>
                <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 6 }}>Subject</label>
                <input name="subject" value={form.subject} onChange={handleChange} placeholder="How can we help?" style={inputStyle} />
              </div>

              <div style={{ marginBottom: 24 }}>
                <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 6 }}>Message *</label>
                <textarea
                  name="message" value={form.message} onChange={handleChange}
                  placeholder="Tell us what you need help with..."
                  rows={6}
                  style={{ ...inputStyle, resize: 'vertical', minHeight: 140 }}
                  required
                />
              </div>

              {status && (
                <div style={{
                  padding: '10px 14px', borderRadius: 8, fontSize: 13, marginBottom: 16,
                  background: status.ok ? 'rgba(16,185,129,0.08)' : 'rgba(239,68,68,0.08)',
                  color: status.ok ? '#059669' : '#dc2626',
                  border: `1px solid ${status.ok ? 'rgba(16,185,129,0.2)' : 'rgba(239,68,68,0.2)'}`,
                }}>
                  {status.text}
                </div>
              )}

              <button
                type="submit"
                disabled={sending}
                style={{
                  width: '100%', padding: '13px 0', background: '#2563eb', color: '#fff',
                  border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 700,
                  cursor: 'pointer', transition: 'background 0.2s',
                }}
              >
                {sending ? 'Opening email client...' : 'Send Message →'}
              </button>

              <p style={{ fontSize: 11, color: '#9ca3af', textAlign: 'center', marginTop: 12 }}>
                This will open your email client pre-filled with your message.
              </p>
            </form>
          </div>
        </div>
      </div>
    </MarketingLayout>
  );
}
